import React from 'react';
import {
  Grid, FormControl, InputLabel, Select, MenuItem, OutlinedInput, Chip, Box, TextField,
} from '@mui/material';
import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined';
import SearchableSelect from './SearchableSelect';
import SectionCard from './SectionCard';
import {
  COUNTRY_OPTIONS,
  GROUP_TYPE_OPTIONS,
  shouldShowGroupGuid,
} from '../../constants/groupSchoolFieldOptions';

// Shared "Classification" body for the group create wizard and the group edit modal:
// Group Type, the MAT-only Group GUID, Customer Country, Data Storage Location and the
// Customer Type multi-select. Controlled like SchoolFormFields — `values` drives it and
// every change routes through `onFieldChange(fieldName, value)`.
//
// `names` maps the logical fields onto whatever keys the host's values use (the wizard is
// PascalCase, the edit modal camelCase). `hideGroupTypeAndGuid` lets the wizard suppress
// the Group Type + GUID pair here, because its step leads with them (paired with the GIAS
// autofill); the wizard then shares only Country/Storage/CustomerType, while the modal
// renders the whole block (its GUID is a plain editable field, no GIAS side-effect).
// `customerTypeOptions` may be the dynamic CustomerType records or plain strings — both are
// normalised, matching the SchoolFormFields treatment.

const DEFAULT_NAMES = {
  groupType: 'GroupType',
  matGroupUID: 'MatGroupUID',
  country: 'Country',
  dataStorageLocation: 'DataStorageLocation',
  customerType: 'CustomerType',
};

const normaliseCustomerTypeOptions = (options = []) =>
  options.map((opt) => {
    if (opt && typeof opt === 'object') {
      return {
        value: opt.value ?? opt.Name ?? '',
        label: opt.label ?? opt.DisplayName ?? opt.Name ?? String(opt.value ?? ''),
      };
    }
    return { value: opt, label: String(opt) };
  });

const parseCustomerType = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) return value;
  try {
    return JSON.parse(value);
  } catch (e) {
    return [];
  }
};

export default function GroupFormFields({
  values,
  onFieldChange,
  names = DEFAULT_NAMES,
  customerTypeOptions = [],
  storageLocations = [],
  hideGroupTypeAndGuid = false,
}) {
  const key = { ...DEFAULT_NAMES, ...names };
  const customerOptions = normaliseCustomerTypeOptions(customerTypeOptions);
  const selectedCustomerTypes = parseCustomerType(values[key.customerType]);
  const labelFor = (value) => {
    const match = customerOptions.find((o) => o.value === value);
    return match ? match.label : value;
  };

  return (
    <SectionCard title="Classification" icon={<CategoryOutlinedIcon fontSize="small" />}>
      <Grid container spacing={1.5}>
      {!hideGroupTypeAndGuid && (
        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <InputLabel id="group-form-type-label">Group Type</InputLabel>
            <Select
              labelId="group-form-type-label"
              label="Group Type"
              value={values[key.groupType] || ''}
              onChange={(e) => onFieldChange(key.groupType, e.target.value)}
            >
              {GROUP_TYPE_OPTIONS.map((opt) => (
                <MenuItem key={opt} value={opt}>{opt}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      )}

      {!hideGroupTypeAndGuid && shouldShowGroupGuid(values[key.groupType]) && (
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Group GUID"
            value={values[key.matGroupUID] || ''}
            onChange={(e) => onFieldChange(key.matGroupUID, e.target.value)}
          />
        </Grid>
      )}

      <Grid item xs={12} md={6}>
        <SearchableSelect
          label="Customer Country"
          value={values[key.country]}
          onChange={(val) => onFieldChange(key.country, val || '')}
          options={COUNTRY_OPTIONS}
        />
      </Grid>

      <Grid item xs={12} md={6}>
        <FormControl fullWidth>
          <InputLabel id="group-form-storage-label">Data Storage Location</InputLabel>
          <Select
            labelId="group-form-storage-label"
            label="Data Storage Location"
            value={values[key.dataStorageLocation] || ''}
            onChange={(e) => onFieldChange(key.dataStorageLocation, e.target.value)}
          >
            {storageLocations.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>

      <Grid item xs={12}>
        <FormControl fullWidth>
          <InputLabel id="group-form-customer-type-label">Customer Type</InputLabel>
          <Select
            labelId="group-form-customer-type-label"
            multiple
            value={selectedCustomerTypes}
            onChange={(e) => onFieldChange(key.customerType, JSON.stringify(e.target.value))}
            input={<OutlinedInput label="Customer Type" />}
            renderValue={(selected) => (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {selected.map((value) => (
                  <Chip key={value} label={labelFor(value)} />
                ))}
              </Box>
            )}
          >
            {customerOptions.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
      </Grid>
    </SectionCard>
  );
}
